import { User } from "../models/User";
import { Contact } from "../models/Contact";
import { TimeZone } from "../models/TimeZone";
import { SessionInfo } from "../models/SessionInfo";
import { FeaturesCode } from "../constants/FeaturesCode";
import { AuthenticateResponse } from "../models/AuthenticateResponse";

export class AuthorizedUser {

    private static readonly AuthorizedUserStorageKey: string = "CToolAuthorizedUser";
    private static readonly LogitudeTokensStorageKey: string = "LogitudeTokens";

    static getId(): number | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.Id;
    }

    static getExternalId(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.ExternalId;
    }

    static getTenant(): number | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.Tenant;
    }

    static getFirstName(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.FirstName;
    }

    static getLastName(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.LastName;
    }

    static getEmail(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.Email;
    }

    static getPartnerTypeCode(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.PartnerTypeCode;
    }

    static getPartnerTypeFeaturePermission(featureCode: string): boolean | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        let featurePermission = authorizedUser.User.PartnerType?.PartnerTypeFeaturesPermissions?.find(e => e.FeatureCode === featureCode);
        if (featurePermission) {
            return featurePermission?.Display;
        }
        return false;
    }

    static isPartnerTypeHasSettingsFeaturePermission(): boolean | null {
        return (
            this.getPartnerTypeFeaturePermission(FeaturesCode.TaskTypes) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.Users) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.Customization) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.ProcessInstances) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.Workflows) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.FieldPermissions) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.FeaturePermissions) ||
            this.getPartnerTypeFeaturePermission(FeaturesCode.AuditLogs)
        );
    }

    static getPartnerId(): number | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.PartnerId;
    }

    static getPartnerExternalId(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.Partner.ExternalId;
    }

    static getTimeZone(): TimeZone | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.TimeZone;
    }

    static getDaylightSavingTime(): boolean | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.DaylightSavingTime;
    }

    static getIsSupervisorAgent(): boolean | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.IsSupervisorAgent;
    }

    static getIsAdministrator(): boolean | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.User.IsAdministrator;
    }

    static getToken(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.Token;
    }

    static getContact(): Contact | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.Contact;
    }

    static getCompanyName(): string | null {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser === null) {
            return null;
        }
        return authorizedUser.CompanyName;
    }

    static getAuthorizedUser(): { User: User, Contact: Contact, Token: string, CompanyName: string } | null {
        let authorizedUserLocalStorage = localStorage.getItem(this.AuthorizedUserStorageKey);
        if (authorizedUserLocalStorage === null) {
            return null;
        }
        return JSON.parse(authorizedUserLocalStorage);
    }

    static setAuthorizedUser(authenticateResponse: AuthenticateResponse): void {
        if (authenticateResponse !== null && authenticateResponse.User !== null && authenticateResponse.Token !== null) {
            let newData = {
                User: authenticateResponse.User,
                Contact: authenticateResponse.Contact,
                Token: authenticateResponse.Token,
                CompanyName: authenticateResponse.CompanyName
            };
            localStorage.setItem(this.AuthorizedUserStorageKey, JSON.stringify(newData));
        }
    }

    static resetAuthorizedUser(): void {
        localStorage.removeItem(this.AuthorizedUserStorageKey);
        localStorage.removeItem(this.LogitudeTokensStorageKey);
        SessionInfo.IsUserAuthorized = false;
        SessionInfo.AuthorizedUserToken = null;
        SessionInfo.IsLogitudeAuthentication = false;
    }

    static shouldValidateToken(): boolean {
        let authorizedUser = this.getAuthorizedUser();
        if (authorizedUser !== null) {
            if (!SessionInfo.IsUserAuthorized || SessionInfo.AuthorizedUserToken !== this.getToken()) {
                return true;
            }
            if (authorizedUser.User?.PartnerType?.PartnerTypeFeaturesPermissions === undefined) {
                return true;
            }
            return false;
        }
        return true;
    }
}
